import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AdditionalNoteFieldsElectricityCreate, AdditionalNoteFieldsGasCreate, AdminChangesLog, AdminChangesSendEmail, BaseInvoiceUpdate, ElectricityInvoice, GasInvoice, HistoricalDataElectricity, HistoricalDataGas, InvoiceElectricityAndCompanyGet, InvoiceElectricityData, InvoiceGasAndCompanyGet, InvoiceGasData, LocationViewUpdatePDR, LocationViewUpdatePOD, ReviewUpdate } from '../interfaces/invoice-interfaces';
import { map, Observable } from 'rxjs';
import { API_ROUTES } from './api-routes';
import { DEFAULT_JSON_HEADERS, DEFAULT_PDF_HEADERS } from './headers.config';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  constructor(private http: HttpClient) { }

  getElectricityInvoice(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.ELECTRICITY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getGasInvoice(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.GAS,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getElectricityDispute(id: string): Observable<any> {
    return this.http.get(
      `${API_ROUTES.INVOICES.ELECTRICITY}/${id}/dispute-report`,
      { responseType: 'blob', headers: DEFAULT_PDF_HEADERS }
    );
  }

  bookElectricityDisputeExpert(id: string): Observable<any> {
    return this.http.post(
      `${API_ROUTES.INVOICES.ELECTRICITY}/${id}/book-expert`,
      null
    )
  }

  getGasDispute(id: string): Observable<any> {
    return this.http.get(
      `${API_ROUTES.INVOICES.GAS}/${id}/dispute-report`,
      { responseType: 'blob' }
    );
  }

  bookGasDisputeExpert(id: string): Observable<any> {
    return this.http.post(
      `${API_ROUTES.INVOICES.GAS}/${id}/book-expert`,
      null
    )
  }

  updateElectricityInvoice(invoice: BaseInvoiceUpdate): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<ElectricityInvoice>(
      `${API_ROUTES.INVOICES.ELECTRICITY}/${invoice.id ?? ''}`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateGasInvoice(invoice: BaseInvoiceUpdate): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<GasInvoice>(
      `${API_ROUTES.INVOICES.GAS}/${invoice.id ?? ''}`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateHistoricalElectricityData(location_id: string, invoice: HistoricalDataElectricity): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<HistoricalDataElectricity>(
      `${API_ROUTES.INVOICES.ELECTRICITY}/${location_id}/historical`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateHistoricalGasData(location_id: string, invoice: HistoricalDataGas): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<HistoricalDataGas>(
      `${API_ROUTES.INVOICES.GAS}/${location_id}/historical`,
      payload,
      { headers: DEFAULT_JSON_HEADERS }
    );
  }

  updateFullElectricityInvoice(invoice: InvoiceElectricityData): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.ELECTRICITY_FULL_UPDATE}/${invoice.id ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  updateFullGasInvoice(invoice: InvoiceGasData): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.GAS_FULL_UPDATE}/${invoice.id ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  updateElectricityLocationPod(id: string, invoice: LocationViewUpdatePOD): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.ELECTRICITY_POD_LOCATION_UPDATE}/${id ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  updateGasLocationPdr(id: string, invoice: LocationViewUpdatePDR): Observable<any> {
    const payload = JSON.stringify(invoice);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.GAS_PDR_LOCATION_UPDATE}/${id ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  // ADMIN
  uploadElectricityJsonUpdate(file: File, fileId: string): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<any>(
      `${API_ROUTES.INVOICES.PROCESSING_RESULT_ELECTRICITY}/${fileId}/upload`,
      formData,
      {
        responseType: 'text' as 'json'
      }
    );
  }

  uploadGasJsonUpdate(file: File, fileId: string): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http.post<any>(
      `${API_ROUTES.INVOICES.PROCESSING_RESULT_GAS}/${fileId}/upload`,
      formData,
      {
        responseType: 'text' as 'json'
      }
    );
  }

  getElectricityReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_ELECTRICITY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getReviewedOnlyElectricityReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_ELECTRICITY_REVIEWED_ONLY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getAllElectricityReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_ELECTRICITY_ALL,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getGasReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_GAS,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getReviewedOnlyGasReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_GAS_REVIEWED_ONLY,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  getAllGasReviewInvoices(next_page: number = 1, per_page: number = 20): Observable<any> {
    const params = new HttpParams()
      .set('next_page', next_page)
      .set('per_page', per_page);

    return this.http.get<any>(
      API_ROUTES.INVOICES.REVIEW_GAS_ALL,
      { params: params, observe: 'response' }
    ).pipe(
      map(response => {
        const totalCount = response.headers?.get('x-total-count') ? Number(response.headers.get('x-total-count')) : 0;
        return { data: response.body.data || [], totalCount: totalCount };
      }));
  }

  updateElectricityInvoiceReviewStatus(invoiceId: string, review: ReviewUpdate): Observable<any> {
    const payload = JSON.stringify(review);

    return this.http.post<any>(
      `${API_ROUTES.INVOICES.REVIEW_ELECTRICITY}/update/${invoiceId}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  updateGasInvoiceReviewStatus(invoiceId: string, review: ReviewUpdate): Observable<any> {
    const payload = JSON.stringify(review);

    return this.http.post<any>(
      `${API_ROUTES.INVOICES.REVIEW_GAS}/update/${invoiceId}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  sendFileChangesLog(fileId: string, changesLog: AdminChangesLog): Observable<any> {
    const payload = JSON.stringify(changesLog);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.REVIEW_ADMIN_CHANGES_LOG}/${fileId ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  sendFileChangesEmail(fileId: string, changesSendEmailData: AdminChangesSendEmail): Observable<any> {
    const payload = JSON.stringify(changesSendEmailData);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.REVIEW_SEND_EMAIL}/${fileId ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS,
        responseType: 'text' as 'json'
      }
    );
  }

  getElectricityInvoiceById(invoiceId: string): Observable<InvoiceElectricityAndCompanyGet> {
    return this.http.get<InvoiceElectricityAndCompanyGet>(
      `${API_ROUTES.INVOICES.ELECTRICITY_SINGLE}/${invoiceId}`
    );
  }

  getGasInvoiceById(invoiceId: string): Observable<InvoiceGasAndCompanyGet> {
    return this.http.get<InvoiceGasAndCompanyGet>(
      `${API_ROUTES.INVOICES.GAS_SINGLE}/${invoiceId}`
    );
  }

  saveAdditionalNoteFieldsElectricity(podId: string, additionalNotes: AdditionalNoteFieldsElectricityCreate): Observable<any> {
    const payload = JSON.stringify(additionalNotes);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.ADDITIONAL_NOTE_FILEDS_ELECTRICITY}/${podId ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS
      }
    );
  }

  saveAdditionalNoteFieldsGas(podId: string, additionalNotes: AdditionalNoteFieldsGasCreate): Observable<any> {
    const payload = JSON.stringify(additionalNotes);

    return this.http.post<string>(
      `${API_ROUTES.INVOICES.ADDITIONAL_NOTE_FILEDS_GAS}/${podId ?? ''}`,
      payload,
      {
        headers: DEFAULT_JSON_HEADERS
      }
    );
  }
}
